<script setup lang="ts">
/*
 * Searchable dropdown built on @headlessui/vue Combobox. Use instead of
 * Select when the option list is long enough to need filtering (e.g. country
 * or nationality lists with 200+ entries). Visually identical to Select.
 */
import { ref, computed, inject, type Ref, type ComputedRef } from 'vue';
import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
} from '@headlessui/vue';
import { Check, ChevronDown } from 'lucide-vue-next';

interface Option {
  label: string;
  value: string;
  disabled?: boolean;
}

const props = withDefaults(
  defineProps<{
    modelValue: string | null;
    options: Option[];
    placeholder?: string;
    disabled?: boolean;
    invalid?: boolean;
    noResultsText?: string;
  }>(),
  { placeholder: 'Search…', noResultsText: 'No results' },
);

const emit = defineEmits<{ 'update:modelValue': [v: string | null] }>();

const field = inject<{
  inputId: Ref<string>;
  describedBy: ComputedRef<string>;
  invalid: ComputedRef<boolean>;
}>('formField', undefined as never);

const isInvalid = computed(() => props.invalid || field?.invalid.value || false);

const query = ref('');

const filtered = computed(() => {
  const q = query.value.toLowerCase().trim();
  if (!q) return props.options;
  return props.options.filter((o) => o.label.toLowerCase().includes(q));
});

function displayValue(val: unknown): string {
  if (!val || typeof val !== 'string') return '';
  return props.options.find((o) => o.value === val)?.label ?? val;
}
</script>

<template>
  <Combobox
    :model-value="modelValue"
    :disabled="disabled"
    nullable
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div class="relative">
      <ComboboxInput
        :id="field?.inputId.value"
        class="w-full rounded-xl border bg-app-surface px-3.5 py-2.5 pr-10 text-base text-app-fg placeholder:text-app-muted transition-shadow outline-none"
        :class="[
          isInvalid
            ? 'border-red-300 focus:ring-3 focus:ring-red-200 focus:border-red-400'
            : 'border-app-border focus:ring-3 focus:ring-primary/20 focus:border-primary',
          disabled ? 'cursor-not-allowed bg-slate-50 text-slate-500' : '',
        ]"
        :placeholder="placeholder"
        :aria-describedby="field?.describedBy.value"
        :aria-invalid="isInvalid || undefined"
        autocomplete="off"
        :display-value="displayValue"
        @change="query = ($event.target as HTMLInputElement).value"
        @focus="query = ''"
      />
      <ComboboxButton
        class="absolute inset-y-0 right-0 flex items-center pr-2"
        :disabled="disabled"
        aria-hidden="true"
      >
        <ChevronDown class="h-4 w-4 text-[#94A3B8]" />
      </ComboboxButton>
      <ComboboxOptions
        class="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-xl border border-app-border bg-app-surface py-1 shadow-popup focus:outline-none"
      >
        <li v-if="filtered.length === 0" class="px-3 py-2 text-md text-app-muted">
          {{ noResultsText }}
        </li>
        <ComboboxOption
          v-for="opt in filtered"
          v-slot="{ active, selected }"
          :key="opt.value"
          :value="opt.value"
          :disabled="opt.disabled"
          as="template"
        >
          <li
            class="flex cursor-pointer items-center gap-2 px-3 py-2 text-md"
            :class="[
              active ? 'bg-primary/5 text-app-fg' : 'text-app-fg',
              opt.disabled ? 'cursor-not-allowed opacity-40' : '',
            ]"
          >
            <Check
              class="h-3.5 w-3.5 shrink-0 text-primary"
              :class="selected ? 'opacity-100' : 'opacity-0'"
              aria-hidden="true"
            />
            {{ opt.label }}
          </li>
        </ComboboxOption>
      </ComboboxOptions>
    </div>
  </Combobox>
</template>
